<template>
  <a-drawer
    :width="900"
    :visible="visible"
    :unmount-on-close="true"
    :mask-closable="false"
    :on-before-ok="handleBeforeOk"
    @cancel="handleCancel"
  >
    <template #title>
      <a-space> {{ $t('customer.addCustomer') }} </a-space>
    </template>

    <a-row>
      <a-col :span="16" :offset="6">
        <a-form ref="formRef" :model="addCustomerForm">
          <a-form-item v-if="!isHidden('phoneType')" label="电话类型">
            <a-radio-group v-model="phoneType">
              <a-radio :value="PHONE_TYPE.DOMESTIC">国内号码</a-radio>
              <a-radio :value="PHONE_TYPE.OVERSEAS">境外号码</a-radio>
            </a-radio-group>
          </a-form-item>
          <a-form-item
            v-if="!isHidden('mobile')"
            field="mobile"
            :label="$t('form.mobile')"
            :rules="mobileRules"
          >
            <a-input
              v-model="addCustomerForm.mobile"
              :placeholder="$t('form.mobile.placeholder')"
            />
          </a-form-item>
          <a-form-item
            v-if="!isHidden('nickname')"
            field="nickname"
            :label="$t('form.nickname')"
          >
            <a-input
              v-model="addCustomerForm.nickname"
              :placeholder="$t('form.nickname.placeholder')"
            />
          </a-form-item>
          <a-form-item
            v-if="!isHidden('city')"
            field="city"
            :label="$t('form.addCustomer.city')"
          >
            <a-input
              v-model="addCustomerForm.city"
              :placeholder="$t('form.addCustomer.city.placeholder')"
            />
          </a-form-item>
          <a-form-item
            v-if="!isHidden('laiyuan')"
            field="laiyuan"
            :label="$t('form.addCustomer.laiyuan')"
          >
            <a-input
              v-model="addCustomerForm.laiyuan"
              :placeholder="$t('form.addCustomer.source.placeholder')"
            />
          </a-form-item>
          <a-form-item
            v-if="parent && !isHidden('uid')"
            field="uid"
            label="上级"
          >
            <a-input
              :model-value="parent?.nickname"
              disabled
              placeholder="请输入上级的昵称(可选)"
              allow-clear
            />
          </a-form-item>
        </a-form>
      </a-col>
    </a-row>
  </a-drawer>
</template>

<script lang="ts" setup>
  import { ref, watch, computed } from 'vue';
  import {
    Message,
    Modal,
    type FieldRule,
    type FormInstance,
  } from '@arco-design/web-vue';
  import { addCustomer } from '@/api/customer';
  import type { BaseCustomer } from '@/types/customer';
  import { isEqual, cloneDeep } from 'lodash';

  /**
   * 常量定义
   */
  const PHONE_TYPE = {
    DOMESTIC: 0,
    OVERSEAS: 1,
  } as const;

  /**
   * Props & Emits
   */
  const props = withDefaults(
    defineProps<{
      visible: boolean;
      initialData?: Partial<BaseCustomer>;
      parent?: BaseCustomer;
      hiddenFields?: string[];
      customSubmit?: (data: Partial<BaseCustomer>) => Promise<any>;
    }>(),
    {
      hiddenFields: () => [],
    }
  );

  const emit = defineEmits<{
    (e: 'update:visible', visible: boolean): void;
    (e: 'success'): void;
  }>();

  /**
   * 状态管理
   */
  const formRef = ref<FormInstance>();
  const isSubmitting = ref(false);

  const generateFormModel = (): Partial<BaseCustomer> => ({
    mobile: props.initialData?.mobile || '',
    nickname: props.initialData?.nickname || '',
    city: props.initialData?.city || '',
    laiyuan: props.initialData?.laiyuan || '',
    uid: props.parent?.id || props.initialData?.uid || undefined,
  });

  const addCustomerForm = ref<Partial<BaseCustomer>>(generateFormModel());
  const initialFormState = ref<Partial<BaseCustomer>>(generateFormModel());
  const phoneType = ref<number>(PHONE_TYPE.DOMESTIC);

  const mobileRules = computed<FieldRule[]>(() => {
    const rules: FieldRule[] = [{ required: true, message: '请输入联系电话' }];
    if (phoneType.value === PHONE_TYPE.DOMESTIC) {
      rules.push({
        match: /^1[3-9]\d{9}$/,
        message: '请输入正确的国内手机号码',
      });
    }
    return rules;
  });

  const isHidden = (field: string) => props.hiddenFields.includes(field);

  const isModified = computed(() => {
    return !isEqual(addCustomerForm.value, initialFormState.value);
  });

  // 监听 Drawer 状态
  watch(
    () => props.visible,
    (newVal) => {
      if (newVal) {
        // 打开时根据最新 props 重新初始化表单
        const newModel = generateFormModel();
        addCustomerForm.value = cloneDeep(newModel);
        initialFormState.value = cloneDeep(newModel);
        isSubmitting.value = false;
      } else {
        // 关闭时重置 UI 状态并清除校验信息
        phoneType.value = PHONE_TYPE.DOMESTIC;
        formRef.value?.clearValidate();
      }
    }
  );

  /**
   * 提交表单
   */
  const handleBeforeOk = async () => {
    if (isSubmitting.value) return false;

    const errors = await formRef.value?.validate();
    if (errors) return false;

    isSubmitting.value = true;
    const params = { ...addCustomerForm.value };

    // 确保关联员工 ID 的正确性
    if (props.parent?.id) {
      params.uid = props.parent.id;
    } else if (!params.uid) {
      delete params.uid;
    }

    try {
      if (props.customSubmit) {
        await props.customSubmit(params);
      } else {
        await addCustomer(params as BaseCustomer);
      }
      Message.success('添加客户成功');
      // 重置表单初始状态以避免触发未保存提示
      initialFormState.value = cloneDeep(addCustomerForm.value);
      emit('success');
      emit('update:visible', false);
      return true;
    } catch (error) {
      console.error('Add customer failed:', error);
      return false;
    } finally {
      isSubmitting.value = false;
    }
  };

  /**
   * 取消关闭
   */
  const handleCancel = () => {
    if (isModified.value) {
      Modal.confirm({
        title: '提示',
        content: '有未保存的数据，确认关闭吗？',
        onOk: () => {
          emit('update:visible', false);
        },
      });
    } else {
      emit('update:visible', false);
    }
  };
</script>

<style scoped></style>
